import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ModalForm } from 'components/ModalForm';
import { useModal } from 'context/ModalProvider/useModal';
import { calcPumpPrice, calcPumpPriceMinimal } from 'pages/RequestTask/Views/TaskDetail/TaskDetail.utils';
import $createDataSource from 'utils/create-data-souce-instance';
import { isSuccess } from 'utils/responseStatus';

import {
  useChangeMethodOfReceptionMutation,
  useGetMethodOfReceptionTypesQuery,
  useLazyGetMethodOfReceptionDetailQuery,
} from 'bus/methodOfReception/services';
import { MethodOfReception } from 'bus/methodOfReception/types';
import { useGetRequestQuery, useLazyRequestPumpPriceQuery } from 'bus/request';
import { RequestPriceResponse } from 'bus/request/types';

import { MethodOfReceptionForm } from './MethodOfReceptionForm';
import { METHOD_OF_RECEPTION_INIT_FORM } from './MethodOfReceptionModal.constants';

type Props = {
  onClose: () => void;
  refresh: () => void;
  methodReceptionId: number;
  requestId: string | number;
};

export const MethodOfReceptionEditModal: FC<Props> = ({ onClose, refresh, methodReceptionId, requestId }) => {
  const validationRef = useRef<any>(null);
  const { hideModal } = useModal();
  const { data: dataRequest } = useGetRequestQuery({ id: requestId });
  const { data: methodTypes } = useGetMethodOfReceptionTypesQuery();
  const [methodOfReceptionDetail] = useLazyGetMethodOfReceptionDetailQuery();
  const [changeMethodOfReception, { isLoading: isLoadingChange }] = useChangeMethodOfReceptionMutation();
  const [requestPumpPrice, { data: pumpPrice }] = useLazyRequestPumpPriceQuery();

  const storeTransport: any = useMemo(() => {
    const defaultFilter = [['transportType', '=', 0], 'and', ['id', '>', 0]];
    return $createDataSource('/v1/Transport', 'id', { defaultFilter });
  }, []);

  const storeEmployee: any = useMemo(() => {
    const defaultFilter = [['postId', '=', 1]];
    const defaultSort = [{ selector: 'fullName', desc: false }];
    return $createDataSource('/v1/employee', 'id', { defaultFilter, defaultSort });
  }, []);

  const [form, setForm] = useState<MethodOfReception>(METHOD_OF_RECEPTION_INIT_FORM);
  const [dataRequestInfo, setDataRequestInfo] = useState<any>(null);

  const handleRequestPumpPrice = useCallback(
    (nextForm: MethodOfReception) => {
      if (
        nextForm.type !== 3 ||
        !dataRequest?.date ||
        !dataRequest?.factoryId ||
        !dataRequest?.objectId ||
        !nextForm?.transportId
      ) {
        return;
      }
      requestPumpPrice({
        date: dataRequest.date,
        factoryId: dataRequest.factoryId,
        objectId: dataRequest.objectId,
        transportId: nextForm.transportId,
      })
        .unwrap()
        .then((data) => {
          if (isSuccess(data)) {
            setForm((prev) => {
              const currentPumpMoving = () => {
                if (prev.pumpMovingCount == 0) {
                  return 0;
                }

                return prev.pumpMovingFilledManually ? prev.pumpMoving : data.pumpMoving;
              };

              const currentPumpPipes = () => {
                if (prev.pumpPipesCount == 0) {
                  return 0;
                }

                return prev.pumpPipesFilledManually ? prev.pumpPipes : data.pumpPipes;
              };

              const currentDowntime = () => {
                if (prev.downtimeHours == 0) {
                  return 0;
                }

                return prev.downtimeFilledManually ? prev.downtime : data.downtime;
              };

              const currentPumpQuencher = () => {
                if (!prev.useQuencher) {
                  return 0;
                }

                return prev.pumpQuencherFilledManually ? prev.pumpQuencher : data.pumpQuencher;
              };

              return {
                ...prev,
                pumpPriceMinimal: calcPumpPriceMinimal(prev, data),
                pumpPrice: calcPumpPrice(prev, data),
                pumpInnings: prev.pumpInningsFilledManually ? prev.pumpInnings : data.pumpInnings,
                pumpQuencher: currentPumpQuencher(),
                pumpMoving: currentPumpMoving(),
                pumpPipes: currentPumpPipes(),
                downtime: currentDowntime(),
              };
            });
          }
        });
    },
    [dataRequest, requestPumpPrice],
  );

  const handleChangeForm = useCallback(
    (newValue: Partial<MethodOfReception>) => {
      setForm((prev) => {
        if (!prev) return prev;

        let nextForm = { ...prev, ...newValue };

        if ('pumpQuantity' in newValue || 'isFilledManually' in newValue) {
          nextForm = {
            ...prev,
            ...newValue,
            pumpPriceMinimal: pumpPrice ? calcPumpPriceMinimal(prev, pumpPrice) : 0,
            pumpPrice: pumpPrice ? calcPumpPrice(prev, pumpPrice) : 0,
          };
        }

        if (
          ('type' in newValue || 'transportId' in newValue) &&
          nextForm.type === 3 &&
          dataRequest?.date &&
          dataRequest?.factoryId &&
          dataRequest?.objectId &&
          nextForm.transportId
        ) {
          handleRequestPumpPrice(nextForm);
        }
        return nextForm;
      });
    },
    [dataRequest?.date, dataRequest?.factoryId, dataRequest?.objectId, handleRequestPumpPrice, pumpPrice],
  );

  useEffect(() => {
    methodOfReceptionDetail({ id: methodReceptionId })
      .unwrap()
      .then((data) => {
        setForm(data);
        setDataRequestInfo(data);
        handleRequestPumpPrice(data);
      });
  }, [methodReceptionId, methodOfReceptionDetail, handleRequestPumpPrice]);

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();
    if (validationRef.current?.instance.validate().isValid) {
      const isFilledManually = dataRequestInfo?.pumpQuantity !== form.pumpQuantity || form.isFilledManually;
      changeMethodOfReception({ ...form, id: methodReceptionId, requestId: Number(requestId), isFilledManually });
      refresh();
      onClose();
    }
  };

  const clearButton = (name: keyof RequestPriceResponse) => {
    const old = pumpPrice?.[name];
    setForm((prev) => (prev ? { ...prev, [name]: old } : prev));
  };

  const totalValue =
    (form.pumpQuantity <= Number(pumpPrice?.minQuantity) ? form.pumpPriceMinimal : form.pumpPrice * form.pumpQuantity) +
    (form.pumpDelivery ?? 0) +
    (form.pumpPipes ?? 0) * Number(form.pumpPipesCount) +
    (form.pumpInnings ?? 0) +
    (form.pumpMoving ?? 0) * Number(form.pumpMovingCount) +
    (form.useQuencher ? form.pumpQuencher ?? 0 : 0) +
    (form.downtime ?? 0) * Number(form.downtimeHours);

  if (!form) return null;

  return (
    <ModalForm
      onHidden={hideModal}
      title="Метод приема"
      visible
      isLoadingSubmit={isLoadingChange}
      width={900}
      height="auto"
      textConfirm="Сохранить"
      hideOnOutsideClick={false}
      onSubmit={handleSubmit}
      ref={validationRef}
    >
      <MethodOfReceptionForm
        form={form}
        methodTypes={methodTypes || []}
        storeTransport={storeTransport}
        storeEmployee={storeEmployee}
        totalValue={totalValue}
        handleChangeForm={handleChangeForm}
        clearButton={clearButton}
        pumpPrice={pumpPrice}
      />
    </ModalForm>
  );
};
